import React, { useState, useEffect, useRef } from 'react';
import { AudioEngine } from '../audio/AudioEngine';
import { SPM_FIGURE_METADATA } from '../data/ipccData';
import { SymphonyConductorStage } from './SymphonyConductorStage';
import { Play, Pause, Disc, Music, Sliders, Activity, Volume2, VolumeX, Sparkles, Layers, RotateCcw, ShieldCheck } from 'lucide-react';

interface GrandSymphonyViewProps {
  isGlobalPlaying: boolean;
  onToggleGlobalPlay: () => void;
  onSelectFigure?: (figureId: string) => void;
}

interface SymphonyMovement {
  id: number;
  title: string;
  subtitle: string;
  figures: string[];
  description: string;
  mode: string;
  modeColor: string;
}

const SYMPHONY_MOVEMENTS: SymphonyMovement[] = [
  {
    id: 1,
    title: 'Movement I: The Human Signal',
    subtitle: 'Paleo Baseline & Anthropogenic Radiative Forcing',
    figures: ['spm1', 'spm2'],
    description: 'Sonifies 2000 years of global climate baseline followed by modern human emissions drivers overriding natural solar/volcanic variation.',
    mode: 'C Lydian Mode (0.0°C Baseline)',
    modeColor: 'text-cyan-300 border-cyan-500/40 bg-cyan-950/40'
  },
  {
    id: 2,
    title: 'Movement II: Regional Extremes & Risk Matrix',
    subtitle: 'Spatial Hex-Grid Transients & 33 Impact-Drivers',
    figures: ['spm3', 'spm9'],
    description: 'Acoustic spatial scanning across 45 WGI land regions and 33 physical climate impact-driver categories.',
    mode: 'C Mixolydian Mode (+0.8°C Suspensive)',
    modeColor: 'text-amber-300 border-amber-500/40 bg-amber-950/40'
  },
  {
    id: 3,
    title: 'Movement III: Future Scenarios & Spatial Warming',
    subtitle: 'Polyphonic SSP Divergence & Polar Amplification',
    figures: ['spm4', 'spm5'],
    description: 'Polyphonic 5-SSP trajectory fan divergence paired with South-to-North latitude polar amplification sweeps.',
    mode: 'C Dorian Mode (+1.8°C Somber Minor)',
    modeColor: 'text-orange-300 border-orange-500/40 bg-orange-950/40'
  },
  {
    id: 4,
    title: 'Movement IV: Carbon Metronome & Frequency Accelerations',
    subtitle: 'Cumulative CO2 Rhythmic Pulse & Extreme Weather Multipliers',
    figures: ['spm6', 'spm7'],
    description: 'Carbon metronome pulsing per 100 GtCO2 emitted, accelerating extreme heat metronomes up to 39.2x speed.',
    mode: 'C Locrian Mode (+2.8°C Dissonant)',
    modeColor: 'text-rose-300 border-rose-500/40 bg-rose-950/40'
  },
  {
    id: 5,
    title: 'Movement V: Multi-Century Commitment & Carbon Budget',
    subtitle: 'Sub-Bass Sea Level Drone & Harmonic Allowance Decay',
    figures: ['spm8', 'spm10'],
    description: 'Sub-harmonic sea level rise drone extending to 2300 paired with low-pass ladder filtering as the remaining 1.5°C carbon allowance exhausts.',
    mode: 'C Diminished Mode (+4.0°C Collapse)',
    modeColor: 'text-purple-300 border-purple-500/40 bg-purple-950/40'
  }
];

export const GrandSymphonyView: React.FC<GrandSymphonyViewProps> = ({
  isGlobalPlaying,
  onToggleGlobalPlay,
  onSelectFigure
}) => {
  const audioEngine = AudioEngine.getInstance();
  const [activeMovementIdx, setActiveMovementIdx] = useState<number>(0);
  const [symphonyProgress, setSymphonyProgress] = useState<number>(0);
  const [playbackMode, setPlaybackMode] = useState<'sequential' | 'orchestral'>('sequential');
  const [tempoSpeed, setTempoSpeed] = useState<'adagio' | 'andante' | 'moderato'>('adagio');
  
  // Channel controls state
  const [channelGains, setChannelGains] = useState<Record<string, number>>({});
  const [mutedChannels, setMutedChannels] = useState<Record<string, boolean>>({});
  const [soloChannel, setSoloChannel] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animFrameRef = useRef<number | null>(null);

  // Initialize channel states
  useEffect(() => {
    const initialGains: Record<string, number> = {};
    const initialMutes: Record<string, boolean> = {};
    for (let i = 1; i <= 10; i++) {
      const figId = `spm${i}`;
      initialGains[figId] = audioEngine.getChannelGain(figId);
      initialMutes[figId] = audioEngine.isChannelMuted(figId);
    }
    setChannelGains(initialGains);
    setMutedChannels(initialMutes);
  }, []);

  const [isInterMovementPause, setIsInterMovementPause] = useState<boolean>(false);

  // 1. Conductor Progress Timer (Pure Timer)
  useEffect(() => {
    if (!isGlobalPlaying || isInterMovementPause) return;

    audioEngine.unlock();
    // Step increment based on selected tempo:
    // Adagio = ~60s/movement (0.0033 per 200ms)
    // Andante = ~30s/movement (0.0066 per 200ms)
    // Moderato = ~15s/movement (0.0133 per 200ms)
    const stepInc = tempoSpeed === 'adagio' ? 0.0033 : tempoSpeed === 'andante' ? 0.0066 : 0.0133;

    const interval = setInterval(() => {
      setSymphonyProgress((prevProgress) => prevProgress + stepInc);
    }, 200);

    return () => clearInterval(interval);
  }, [isGlobalPlaying, tempoSpeed, isInterMovementPause]);

  // 2. Sequential Movement Transition Effect with 3.0s Breathing Pause
  useEffect(() => {
    if (symphonyProgress >= 1.0 && !isInterMovementPause) {
      setIsInterMovementPause(true);
      audioEngine.fadeoutAll(1.2); // Smoothly fade synths over 1.2s allowing reverb to decay

      const pauseTimer = setTimeout(() => {
        setSymphonyProgress(0);
        if (playbackMode === 'sequential') {
          setActiveMovementIdx((prevMov) => (prevMov + 1) % 5);
        }
        setIsInterMovementPause(false);
      }, 3000); // 3.0-second inter-movement breathing pause

      return () => clearTimeout(pauseTimer);
    }
  }, [symphonyProgress, playbackMode, isInterMovementPause]);

  // 3. Audio Engine Sound Audition Effect
  useEffect(() => {
    if (isGlobalPlaying && !isInterMovementPause) {
      audioEngine.playSymphonyMovement(activeMovementIdx, symphonyProgress);
    }
  }, [isGlobalPlaying, activeMovementIdx, symphonyProgress, isInterMovementPause]);

  // Real-time Audio Oscilloscope Canvas Visualizer
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const renderSpectrum = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const width = canvas.width;
      const height = canvas.height;

      // Draw Grid Lines
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.stroke();

      // Dynamic Animated Waveform Simulation
      if (isGlobalPlaying) {
        ctx.beginPath();
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = '#38bdf8';
        const time = Date.now() * 0.005;

        for (let x = 0; x < width; x += 2) {
          const mFreq = (activeMovementIdx + 1) * 2;
          const y = height / 2 + Math.sin(x * 0.03 * mFreq + time) * (height * 0.35) * Math.cos(x * 0.01 + time * 0.5);
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      animFrameRef.current = requestAnimationFrame(renderSpectrum);
    };

    renderSpectrum();
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isGlobalPlaying, activeMovementIdx]);

  const handleGainChange = (figId: string, val: number) => {
    setChannelGains((prev) => ({ ...prev, [figId]: val }));
    audioEngine.setChannelGain(figId, val);
  };

  const handleToggleMute = (figId: string) => {
    const isMuted = audioEngine.toggleChannelMute(figId);
    setMutedChannels((prev) => ({ ...prev, [figId]: isMuted }));
  };

  const handleToggleSolo = (figId: string) => {
    const currentSolo = audioEngine.getSoloChannel();
    const newSolo = currentSolo === figId ? null : figId;
    audioEngine.setSoloChannel(newSolo);
    setSoloChannel(newSolo);
  };

  const activeMovement = SYMPHONY_MOVEMENTS[activeMovementIdx];

  return (
    <div className="w-full max-w-7xl mx-auto space-y-8 animate-fade-in pb-12">
      {/* Grand Title Banner */}
      <div className="glass-panel p-6 lg:p-8 rounded-3xl border border-cyan-500/40 relative overflow-hidden space-y-4 shadow-2xl">
        <div className="absolute -right-16 -top-16 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex items-center justify-between flex-wrap gap-4 relative z-10">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-cyan-500/30 text-slate-950 font-black flex-shrink-0">
              <Music className="w-8 h-8 animate-bounce" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-lg text-xs font-mono font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                  AR6 WGI Master Suite
                </span>
                <span className="text-xs font-mono text-slate-400">10-Figure Orchestration</span>
              </div>
              <h2 className="text-2xl lg:text-3xl font-black text-white bg-gradient-to-r from-white via-slate-100 to-cyan-300 bg-clip-text text-transparent mt-1">
                IPCC Grand Climate Symphony
              </h2>
            </div>
          </div>

          {/* Master Suite Controls */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Movement Skip Buttons */}
            {playbackMode === 'sequential' && (
              <div className="flex items-center gap-1.5 bg-slate-900/90 p-1.5 rounded-2xl border border-slate-800">
                <button
                  onClick={() => {
                    setSymphonyProgress(0);
                    setActiveMovementIdx((m) => (m - 1 + 5) % 5);
                  }}
                  className="px-3 py-1.5 rounded-xl text-xs font-mono font-bold bg-slate-800 text-slate-300 hover:text-white transition-colors"
                  title="Previous Movement"
                >
                  ← Prev
                </button>
                <span className="text-xs font-mono font-bold text-cyan-400 px-2">
                  Mov {activeMovementIdx + 1} / 5
                </span>
                <button
                  onClick={() => {
                    setSymphonyProgress(0);
                    setActiveMovementIdx((m) => (m + 1) % 5);
                  }}
                  className="px-3 py-1.5 rounded-xl text-xs font-mono font-bold bg-slate-800 text-slate-300 hover:text-white transition-colors"
                  title="Next Movement"
                >
                  Next →
                </button>
              </div>
            )}

            <button
              onClick={onToggleGlobalPlay}
              className={`flex items-center gap-2.5 px-6 py-3 rounded-2xl font-bold text-sm transition-all shadow-xl ${
                isGlobalPlaying
                  ? 'bg-amber-500 hover:bg-amber-400 text-slate-950 glow-amber'
                  : 'bg-cyan-500 hover:bg-cyan-400 text-slate-950 glow-cyan'
              }`}
            >
              {isGlobalPlaying ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current" />}
              <span>{isGlobalPlaying ? 'Pause Master Symphony' : 'Play Full Composition'}</span>
            </button>
          </div>
        </div>

        {/* Playback Mode Switcher & Tempo Speed Selector */}
        <div className="flex items-center justify-between gap-3 pt-2 relative z-10 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setPlaybackMode('sequential')}
              className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition-all border ${
                playbackMode === 'sequential'
                  ? 'bg-cyan-500 text-slate-950 border-cyan-400 glow-cyan'
                  : 'bg-slate-900 text-slate-400 border-slate-800 hover:text-white'
              }`}
            >
              Sequential 5-Movement Suite (In Order I → V)
            </button>
            <button
              onClick={() => setPlaybackMode('orchestral')}
              className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition-all border ${
                playbackMode === 'orchestral'
                  ? 'bg-purple-500 text-slate-950 border-purple-400 glow-purple'
                  : 'bg-slate-900 text-slate-400 border-slate-800 hover:text-white'
              }`}
            >
              Simultaneous 10-Figure Orchestral Mix
            </button>
          </div>

          {/* Tempo Pace Control */}
          <div className="flex items-center gap-1.5 bg-slate-900/90 p-1.5 rounded-2xl border border-slate-800 text-xs">
            <span className="text-[11px] text-slate-400 font-mono px-2 font-bold flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" /> Tempo:
            </span>
            <button
              onClick={() => setTempoSpeed('adagio')}
              className={`px-2.5 py-1 rounded-lg font-mono font-bold transition-all ${
                tempoSpeed === 'adagio'
                  ? 'bg-amber-500 text-slate-950 glow-amber'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="Unhurried Cinematic Adagio Pace (~60s per movement)"
            >
              Cinematic Adagio (~60s)
            </button>
            <button
              onClick={() => setTempoSpeed('andante')}
              className={`px-2.5 py-1 rounded-lg font-mono font-bold transition-all ${
                tempoSpeed === 'andante'
                  ? 'bg-cyan-500 text-slate-950 glow-cyan'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="Andante Pace (~30s per movement)"
            >
              Andante (~30s)
            </button>
            <button
              onClick={() => setTempoSpeed('moderato')}
              className={`px-2.5 py-1 rounded-lg font-mono font-bold transition-all ${
                tempoSpeed === 'moderato'
                  ? 'bg-purple-500 text-slate-950 glow-purple'
                  : 'text-slate-400 hover:text-white'
              }`}
              title="Moderato Pace (~15s per movement)"
            >
              Moderato (~15s)
            </button>
          </div>
        </div>
      </div>

      {/* Inter-Movement Breathing Pause Status Banner */}
      {isInterMovementPause && (
        <div className="p-3 rounded-2xl bg-cyan-950/90 border border-cyan-500/60 text-cyan-200 flex items-center justify-between shadow-xl animate-pulse text-xs font-mono font-bold">
          <span className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping" />
            <span>⏸️ INTER-MOVEMENT BREATHING PAUSE & REVERB DECAY (Movement {activeMovementIdx + 1} → Movement {((activeMovementIdx + 1) % 5) + 1})</span>
          </span>
          <span className="bg-cyan-900/60 text-cyan-300 px-3 py-1 rounded-xl text-[10px]">3.0s Acoustic Rest</span>
        </div>
      )}

      {/* Focused Conductor Telemetry Stage & Single Focused Vector Diagram */}
      <SymphonyConductorStage
        activeMovementIdx={activeMovementIdx}
        progress={symphonyProgress}
        isPlaying={isGlobalPlaying && !isInterMovementPause}
        onScrubProgress={(newProgress) => {
          setIsInterMovementPause(false);
          setSymphonyProgress(newProgress);
        }}
      />

      {/* 5 Movements Cards Grid */}
      <div className="space-y-4">
        <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
          <Layers className="w-4 h-4 text-cyan-400" />
          <span>The 5 Movements of the Grand Symphony</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {SYMPHONY_MOVEMENTS.map((mov, idx) => {
            const isActive = activeMovementIdx === idx;
            return (
              <button
                key={mov.id}
                onClick={() => {
                  setSymphonyProgress(0);
                  setActiveMovementIdx(idx);
                }}
                className={`p-4 rounded-2xl text-left transition-all border space-y-2 relative overflow-hidden ${
                  isActive
                    ? 'bg-slate-900 border-cyan-500 glow-cyan shadow-xl'
                    : 'bg-slate-950/80 border-slate-800 hover:border-slate-700 hover:bg-slate-900/60'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono font-bold text-cyan-400">MOV {mov.id}</span>
                  {isActive && isGlobalPlaying && <Sparkles className="w-4 h-4 text-amber-400 animate-spin" />}
                </div>
                <h4 className="text-xs font-bold text-white line-clamp-1">{mov.title}</h4>
                <p className="text-[10px] text-slate-400 line-clamp-2 leading-relaxed">{mov.subtitle}</p>

                {isActive && (
                  <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden mt-2">
                    <div
                      className="h-full bg-cyan-400 transition-all duration-100"
                      style={{ width: `${Math.min(100, symphonyProgress * 100)}%` }}
                    />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* 10-Channel Figure Audio Mixing Desk */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <Sliders className="w-5 h-5 text-purple-400" />
            <h3 className="text-sm font-bold text-white">10-Channel AR6 Figure Audio Mixing Console</h3>
          </div>
          <span className="text-xs font-mono text-slate-400">Independent Channel Gain, Mute & Solo</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-3 pt-2">
          {Array.from({ length: 10 }).map((_, i) => {
            const figId = `spm${i + 1}`;
            const meta = SPM_FIGURE_METADATA[figId];
            const gain = channelGains[figId] ?? 0.8;
            const isMuted = mutedChannels[figId] ?? false;
            const isSolo = soloChannel === figId;

            // Check if this channel belongs to the currently playing movement figure pair
            const activePair = [
              ['spm1', 'spm2'],
              ['spm3', 'spm9'],
              ['spm4', 'spm5'],
              ['spm6', 'spm7'],
              ['spm8', 'spm10']
            ][activeMovementIdx] ?? [];

            const isChannelActiveOnStage = isGlobalPlaying && activePair.includes(figId) && !isMuted;

            return (
              <div
                key={figId}
                className={`p-3 rounded-2xl bg-slate-950 border text-center space-y-3 transition-all relative overflow-hidden ${
                  isChannelActiveOnStage
                    ? 'border-cyan-400 bg-cyan-950/20 shadow-lg glow-cyan'
                    : isSolo
                    ? 'border-amber-400 bg-amber-950/20'
                    : isMuted
                    ? 'border-red-900/40 opacity-50'
                    : 'border-slate-800 hover:border-slate-700'
                }`}
              >
                {/* Live VU Meter LED Ring */}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono font-bold text-cyan-300">{meta?.number}</span>
                  <span
                    className={`w-2 h-2 rounded-full transition-all ${
                      isChannelActiveOnStage
                        ? 'bg-cyan-400 shadow-lg animate-ping'
                        : isMuted
                        ? 'bg-red-500'
                        : 'bg-slate-700'
                    }`}
                    title={isChannelActiveOnStage ? 'Audio Active' : 'Idle'}
                  />
                </div>

                <div className="text-[9px] font-semibold text-slate-300 line-clamp-1">{meta?.title}</div>

                {/* Vertical Gain Slider */}
                <div className="h-28 flex items-center justify-center">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : gain}
                    onChange={(e) => handleGainChange(figId, parseFloat(e.target.value))}
                    className="h-24 -rotate-90 accent-cyan-400 cursor-pointer"
                  />
                </div>

                <div className="text-[10px] font-mono text-slate-400">{Math.round(gain * 100)}%</div>

                {/* Mute, Solo & Direct Figure Navigation Button */}
                <div className="space-y-1.5 pt-1">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => handleToggleMute(figId)}
                      className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-bold ${
                        isMuted ? 'bg-red-500 text-slate-950' : 'bg-slate-900 text-slate-400 hover:text-white'
                      }`}
                      title="Mute Channel"
                    >
                      M
                    </button>
                    <button
                      onClick={() => handleToggleSolo(figId)}
                      className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-bold ${
                        isSolo ? 'bg-amber-500 text-slate-950' : 'bg-slate-900 text-slate-400 hover:text-white'
                      }`}
                      title="Solo Channel"
                    >
                      S
                    </button>
                  </div>

                  {onSelectFigure && (
                    <button
                      onClick={() => onSelectFigure(figId)}
                      className="w-full py-1 rounded-lg text-[9px] font-mono font-bold bg-slate-900 hover:bg-cyan-500 hover:text-slate-950 text-cyan-400 border border-slate-800 transition-all flex items-center justify-center gap-0.5"
                      title={`Inspect ${meta?.number} Details`}
                    >
                      <span>View {meta?.number}</span>
                      <span>➔</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
